
import { useState, useMemo, useEffect } from 'react';
import { ArrowUpRight, Copy, ArrowLeftRight, Sparkles } from 'lucide-react';
import { colorEngine, type ColorComparisonResult, type Illuminant } from '../lib/colorEngine';
import { ColorAnimationPanel } from './ColorAnimationPanel';
import { useToolState } from '../contexts/ToolStateContext';
import { useColorActions } from '../hooks/useColorActions';
import { ColorInput } from './ui/ColorInput';
import { InfoIconTooltip } from './Tooltip';

interface ColorComparePanelProps {
  initialColor1?: string;
  initialColor2?: string;
  copiedField: string | null;
  onCopy: (text: string, field: string) => void;
}

export function ColorComparePanel({ 
  initialColor1 = '#3b82f6', 
  initialColor2 = '#ffffff',
  copiedField,
  onCopy 
}: ColorComparePanelProps) {
  const { getToolState, setToolState } = useToolState();

  // Restore persisted state or use defaults
  const persisted = getToolState('compare');
  const [color1Input, setColor1Input] = useState(initialColor1);
  const [color2Input, setColor2Input] = useState(persisted?.color2Input ?? initialColor2);
  const [selectedIlluminant, setSelectedIlluminant] = useState<Illuminant>(persisted?.selectedIlluminant ?? 'D65');

  const [showAnimation, setShowAnimation] = useState(persisted?.showAnimation ?? false);

  // Sync Color 1 with the active color from the ContextBar
  useEffect(() => {
    if (initialColor1) {
      setColor1Input(initialColor1);
    }
  }, [initialColor1]);

  // Sync Color 2 when explicitly provided via deep-link
  useEffect(() => {
    if (initialColor2 && initialColor2 !== '#ffffff') {
      setColor2Input(initialColor2);
    }
  }, [initialColor2]);

  // Persist state on change
  useEffect(() => {
    setToolState('compare', {
      color2Input,
      selectedIlluminant,
      showAnimation,
    });
  }, [color2Input, selectedIlluminant, showAnimation, setToolState]);

  const { setActiveColor } = useColorActions();

  const comparison = useMemo<ColorComparisonResult | null>(() => {
    try {
      const result = colorEngine.compareColors(color1Input, color2Input);
      return result;
    } catch {
      return null;
    }
  }, [color1Input, color2Input]);

  const error = useMemo(() => {
    if (!comparison) {
      if (color1Input.trim() && color2Input.trim()) {
        return 'Invalid color format in one or both inputs';
      }
      return null;
    }
    return null;
  }, [comparison, color1Input, color2Input]);

  const swapColors = () => {
    const temp = color1Input;
    setColor1Input(color2Input);
    setColor2Input(temp);
  };

  const getDeltaEInterpretation = (deltaE: number): string => {
    if (deltaE < 1) return 'Imperceptible';
    if (deltaE < 2) return 'Barely perceptible';
    if (deltaE < 5) return 'Perceptible';
    if (deltaE < 10) return 'Noticeable';
    return 'Very different';
  };

  const getDeltaEInterpretationClass = (deltaE: number): string => {
    if (deltaE < 1) return 'pass';
    if (deltaE < 2) return 'close';
    if (deltaE < 5) return 'moderate';
    if (deltaE < 10) return 'noticeable';
    return 'different';
  };

  const displayColors = useMemo(() => {
    if (!comparison) return null;
    
    const illuminantSim = comparison.illuminantSimulations[selectedIlluminant.toLowerCase() as 'd50' | 'd65'];
    return {
      color1: illuminantSim.color1,
      color2: illuminantSim.color2,
    };
  }, [comparison, selectedIlluminant]);

  return (
    <section className="cmp-section">
      <header>
        <h3 style={{ fontSize: 'var(--t-h3)', fontWeight: 'var(--w-semi)', color: 'var(--ink)', marginBottom: 'var(--s-2)' }}>
          Color Compare Mode
        </h3>
        <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-2)' }}>
          Compare two colors side-by-side with ΔE metrics and illuminant simulation
        </p>
      </header>

      {/* Color Inputs — using ColorInput */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <ColorInput
            value={color1Input}
            onChange={setColor1Input}
            compact
            label="Color 1"
            placeholder="e.g., #3b82f6"
          />
        </div>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <ColorInput
              value={color2Input}
              onChange={setColor2Input}
              compact
              label="Color 2"
              placeholder="e.g., #ffffff"
            />
          </div>
          <button
            type="button"
            onClick={swapColors}
            className="cmp-swap-btn"
            title="Swap colors"
          >
            <ArrowLeftRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && (
        <div style={{ fontSize: 'var(--t-body)', color: 'var(--bad)', background: 'oklch(0.97 0.02 25)', border: '1px solid var(--bad)', borderRadius: 'var(--radius-lg)', padding: 'var(--s-3)' }}>
          {error}
        </div>
      )}

      {comparison && displayColors && (
        <>
          {/* Illuminant Selector */}
          <div className="cmp-illuminant-bar">
            <label className="cmp-illuminant-bar__label">
              Illuminant:
            </label>
            <div className="cmp-illuminant-bar__group">
              <button
                type="button"
                onClick={() => setSelectedIlluminant('D65')}
                className={`cmp-illuminant-bar__btn${selectedIlluminant === 'D65' ? ' cmp-illuminant-bar__btn--active' : ''}`}
              >
                D65 (Daylight)
              </button>
              <button
                type="button"
                onClick={() => setSelectedIlluminant('D50')}
                className={`cmp-illuminant-bar__btn${selectedIlluminant === 'D50' ? ' cmp-illuminant-bar__btn--active' : ''}`}
              >
                D50 (Horizon)
              </button>
            </div>
          </div>

          {/* Side-by-Side Color Chips */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Color 1 Swatch */}
            <div className="space-y-3">
              <div className="cmp-swatch-wrap">
                <div
                  className="cmp-swatch"
                  style={{ backgroundColor: displayColors.color1 }}
                />
                <button
                  className="cmp-swatch__use-btn"
                  onClick={() => setActiveColor({ hex: comparison.color1 })}
                  title="Use as active color"
                  aria-label={`Use ${comparison.color1} as active color`}
                >
                  <ArrowUpRight size={14} />
                  Use
                </button>
              </div>
              <div className="text-center">
                <button
                  className="cmp-hex-label"
                  onClick={() => onCopy(displayColors.color1, 'compare-color1-illum')}
                >
                  {copiedField === 'compare-color1-illum' ? 'Copied ✓' : displayColors.color1}
                </button>
                <div className="cmp-swatch__caption">
                  Color 1 under {selectedIlluminant}
                  {displayColors.color1 !== comparison.color1 && (
                    <> (<span className="cmp-swatch__original">{comparison.color1}</span>)</>
                  )}
                </div>
              </div>
            </div>

            {/* Color 2 Swatch */}
            <div className="space-y-3">
              <div className="cmp-swatch-wrap">
                <div
                  className="cmp-swatch"
                  style={{ backgroundColor: displayColors.color2 }}
                />
                <button
                  className="cmp-swatch__use-btn"
                  onClick={() => setActiveColor({ hex: comparison.color2 })}
                  title="Use as active color"
                  aria-label={`Use ${comparison.color2} as active color`}
                >
                  <ArrowUpRight size={14} />
                  Use
                </button>
              </div>
              <div className="text-center">
                <button
                  className="cmp-hex-label"
                  onClick={() => onCopy(displayColors.color2, 'compare-color2-illum')}
                >
                  {copiedField === 'compare-color2-illum' ? 'Copied ✓' : displayColors.color2}
                </button>
                <div className="cmp-swatch__caption">
                  Color 2 under {selectedIlluminant}
                  {displayColors.color2 !== comparison.color2 && (
                    <> (<span className="cmp-swatch__original">{comparison.color2}</span>)</>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Animation Toggle */}
          <button
            type="button"
            onClick={() => setShowAnimation(!showAnimation)}
            className="cmp-anim-toggle"
          >
            <Sparkles className="w-4 h-4" />
            {showAnimation ? 'Hide' : 'Show'} Color Animation Preview
          </button>

          {/* Animation Panel — collapsible, default closed */}
          {showAnimation && (
            <ColorAnimationPanel
              color1={comparison.color1}
              color2={comparison.color2}
              onCopy={onCopy}
              copiedField={copiedField}
            />
          )}

          {/* ΔE Metrics */}
          <div className="cmp-metric-section">
            <div className="flex items-center gap-2">
              <h4 className="cmp-metric-section__title">
                Color Difference Metrics (ΔE)
              </h4>
              <InfoIconTooltip
                content="ΔE measures perceptual color difference. Lower values = more similar. ΔE2000 is the most accurate modern standard."
                position="top"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ΔE76 */}
              <div className="cmp-metric-card">
                <div className="flex items-center justify-between">
                  <h5 className="cmp-metric-card__label">
                    ΔE76 (CIE 1976)
                  </h5>
                  <button
                    className="cmp-metric-card__copy-btn"
                    onClick={() => onCopy(comparison.deltaE76.toFixed(2), 'deltaE76')}
                    title="Copy to clipboard"
                  >
                    <Copy className="w-3 h-3" />
                    {copiedField === 'deltaE76' ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
                <button
                  className="cmp-metric-card__value"
                  onClick={() => onCopy(comparison.deltaE76.toFixed(2), 'deltaE76')}
                >
                  {copiedField === 'deltaE76' ? 'Copied ✓' : comparison.deltaE76.toFixed(2)}
                </button>
                <div className={`cmp-metric-card__interpretation cmp-metric-card__interpretation--${getDeltaEInterpretationClass(comparison.deltaE76)}`}>
                  {getDeltaEInterpretation(comparison.deltaE76)}
                </div>
              </div>

              {/* ΔE94 */}
              <div className="cmp-metric-card">
                <div className="flex items-center justify-between">
                  <h5 className="cmp-metric-card__label">
                    ΔE94 (CIE 1994)
                  </h5>
                  <button
                    className="cmp-metric-card__copy-btn"
                    onClick={() => onCopy(comparison.deltaE94.toFixed(2), 'deltaE94')}
                    title="Copy to clipboard"
                  >
                    <Copy className="w-3 h-3" />
                    {copiedField === 'deltaE94' ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
                <button
                  className="cmp-metric-card__value"
                  onClick={() => onCopy(comparison.deltaE94.toFixed(2), 'deltaE94')}
                >
                  {copiedField === 'deltaE94' ? 'Copied ✓' : comparison.deltaE94.toFixed(2)}
                </button>
                <div className={`cmp-metric-card__interpretation cmp-metric-card__interpretation--${getDeltaEInterpretationClass(comparison.deltaE94)}`}>
                  {getDeltaEInterpretation(comparison.deltaE94)}
                </div>
              </div>

              {/* ΔE2000 */}
              <div className="cmp-metric-card">
                <div className="flex items-center justify-between">
                  <h5 className="cmp-metric-card__label">
                    ΔE2000 (CIE 2000)
                  </h5>
                  <button
                    className="cmp-metric-card__copy-btn"
                    onClick={() => onCopy(comparison.deltaE2000.toFixed(2), 'deltaE2000')}
                    title="Copy to clipboard"
                  >
                    <Copy className="w-3 h-3" />
                    {copiedField === 'deltaE2000' ? 'Copied ✓' : 'Copy'}
                  </button>
                </div>
                <button
                  className="cmp-metric-card__value"
                  onClick={() => onCopy(comparison.deltaE2000.toFixed(2), 'deltaE2000')}
                >
                  {copiedField === 'deltaE2000' ? 'Copied ✓' : comparison.deltaE2000.toFixed(2)}
                </button>
                <div className={`cmp-metric-card__interpretation cmp-metric-card__interpretation--${getDeltaEInterpretationClass(comparison.deltaE2000)}`}>
                  {getDeltaEInterpretation(comparison.deltaE2000)}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
